// src/components/map/utils/mapHelpers.js

// Pin colors
export const PIN_RED = "#ef4444";
export const PIN_BLUE = "#3b82f6";
export const PIN_GREEN = "#22c55e";

export const STATUS_CONVERTED = "Converted to Order";

// FIPS -> USPS
export const FIPS_TO_USPS = {
  "01": "AL","02": "AK","04": "AZ","05": "AR","06": "CA","08": "CO","09": "CT","10": "DE","11": "DC",
  "12": "FL","13": "GA","15": "HI","16": "ID","17": "IL","18": "IN","19": "IA","20": "KS","21": "KY",
  "22": "LA","23": "ME","24": "MD","25": "MA","26": "MI","27": "MN","28": "MS","29": "MO","30": "MT",
  "31": "NE","32": "NV","33": "NH","34": "NJ","35": "NM","36": "NY","37": "NC","38": "ND","39": "OH",
  "40": "OK","41": "OR","42": "PA","44": "RI","45": "SC","46": "SD","47": "TN","48": "TX","49": "UT",
  "50": "VT","51": "VA","53": "WA","54": "WV","55": "WI","56": "WY",
};

// State name -> USPS
export const NAME_TO_USPS = {
  "ALABAMA":"AL","ALASKA":"AK","ARIZONA":"AZ","ARKANSAS":"AR","CALIFORNIA":"CA","COLORADO":"CO",
  "CONNECTICUT":"CT","DELAWARE":"DE","DISTRICT OF COLUMBIA":"DC","FLORIDA":"FL","GEORGIA":"GA",
  "HAWAII":"HI","IDAHO":"ID","ILLINOIS":"IL","INDIANA":"IN","IOWA":"IA","KANSAS":"KS","KENTUCKY":"KY",
  "LOUISIANA":"LA","MAINE":"ME","MARYLAND":"MD","MASSACHUSETTS":"MA","MICHIGAN":"MI","MINNESOTA":"MN",
  "MISSISSIPPI":"MS","MISSOURI":"MO","MONTANA":"MT","NEBRASKA":"NE","NEVADA":"NV","NEW HAMPSHIRE":"NH",
  "NEW JERSEY":"NJ","NEW MEXICO":"NM","NEW YORK":"NY","NORTH CAROLINA":"NC","NORTH DAKOTA":"ND","OHIO":"OH",
  "OKLAHOMA":"OK","OREGON":"OR","PENNSYLVANIA":"PA","RHODE ISLAND":"RI","SOUTH CAROLINA":"SC","SOUTH DAKOTA":"SD",
  "TENNESSEE":"TN","TEXAS":"TX","UTAH":"UT","VERMONT":"VT","VIRGINIA":"VA","WASHINGTON":"WA","WEST VIRGINIA":"WV",
  "WISCONSIN":"WI","WYOMING":"WY",
};

export const safeArray = (v) => (Array.isArray(v) ? v : []);
export const norm5 = (z) => String(z || "").trim().slice(0, 5);

export const chunk = (arr, size) => {
  const out = [];
  const a = Array.isArray(arr) ? arr : [];
  for (let i = 0; i < a.length; i += size) out.push(a.slice(i, i + size));
  return out;
};

// Accept "Florida" or "FL"
export const stateToCode = (v) => {
  const raw = String(v || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return NAME_TO_USPS[raw] || raw;
};

export const getAssignedInternalUserId = (acc = {}) =>
  acc.assignedToId || acc.assignedUserId || acc.repUserId || acc.salesRepId || acc.userId || null;

export const getUserTerritory = (u = {}) => {
  const id = u.territoryId || u.territory?.id || u.territory || u.territoryID || null;
  const name =
    u.territoryName || u.territory?.name || u.territoryLabel || u.territoryTitle || null;
  return { id: id || null, name: name || null };
};

export const getSampleAccountId = (data = {}) =>
  data.accountId || data.accountID || data.customerId || (data.accountRef && data.accountRef.id) || null;

// Territory matching
export const accountMatchesTerritory = (point, territory) => {
  if (!territory || !point) return false;

  const pointTerritoryId = point.territoryId || null;
  const pointOverrideOn = point.territoryOverride === true;

  if (pointOverrideOn && pointTerritoryId) return pointTerritoryId === territory.id;
  if (pointTerritoryId && pointTerritoryId === territory.id) return true;

  const territoryStates = safeArray(territory.states).map(stateToCode).filter(Boolean);
  const territoryZips = safeArray(territory.zipCodes).map(norm5).filter(Boolean);
  const territoryCities = safeArray(territory.cities)
    .map((c) => String(c || "").trim().toLowerCase())
    .filter(Boolean);

  const hasStateCriteria = territoryStates.length > 0;
  const hasZipCriteria = territoryZips.length > 0;
  const hasCityCriteria = territoryCities.length > 0;

  if (!hasStateCriteria && !hasZipCriteria && !hasCityCriteria) return false;

  const state = stateToCode(point.state);
  const zip = norm5(point.zip);
  const city = String(point.city || "").trim().toLowerCase();

  const matchesState = hasStateCriteria && state ? territoryStates.includes(state) : false;
  const matchesZip = hasZipCriteria && zip ? territoryZips.includes(zip) : false;
  const matchesCity = hasCityCriteria && city ? territoryCities.includes(city) : false;

  return matchesState || matchesZip || matchesCity;
};

export const pointHasTerritoryCoverage = (point, territories) => {
  return (territories || []).some((territory) => accountMatchesTerritory(point, territory));
};

// Enrich points with account + sample data
export const enrichPoints = (points, accountsById, usersById, sampleData) => {
  const {
    sampleAccountIds,
    sampleCountsByAccount,
    convertedCountsByAccount,
    sampleSummariesByAccount,
  } = sampleData;

  return (points || [])
    .filter((p) => typeof p.lat === "number" && typeof p.lng === "number")
    .filter((p) => !!accountsById[p.id])
    .map((p) => {
      const acc = accountsById[p.id] || {};
      const assignedToId = getAssignedInternalUserId(acc);

      const stateFromAcc = acc.address?.state || acc.addressState || acc.state || p.state || null;
      const zipFromAcc = acc.address?.zip || acc.addressZip || acc.zip || null;
      const cityFromAcc = acc.address?.city || acc.addressCity || acc.city || p.city || null;

      const sampleCount = sampleCountsByAccount[p.id] || 0;
      const convertedCount = convertedCountsByAccount[p.id] || 0;

      const storedTerritoryId = acc.territoryId || null;
      const storedOverride = acc.territoryOverride === true;

      const assignedUser = assignedToId ? usersById.get(assignedToId) : null;
      const userTerr = assignedUser ? getUserTerritory(assignedUser) : { id: null, name: null };

      const effectiveTerritoryId =
        storedTerritoryId || (!storedOverride ? userTerr.id || null : null);

      const hasSampleOrder = sampleAccountIds.has(p.id);
      const pinColor = convertedCount > 0 ? PIN_GREEN : sampleCount > 0 ? PIN_BLUE : PIN_RED;

      return {
        ...p,
        name: acc.name || p.name || "Account",
        assignedToId: assignedToId || null,
        state: stateFromAcc,
        zip: zipFromAcc,
        city: cityFromAcc,
        territoryId: storedTerritoryId,
        territoryOverride: storedOverride,
        effectiveTerritoryId,
        hasSampleOrder,
        sampleCount,
        convertedCount,
        pinColor,
        sampleSummaries: sampleSummariesByAccount[p.id] || [],
      };
    });
};

// GeoJSON states layer styling
export const buildStateLayerStyle = (selectedTerritory) => {
  const selectedTerritoryStateSet = selectedTerritory
    ? new Set(safeArray(selectedTerritory.states).map(stateToCode).filter(Boolean))
    : new Set();

  const hasSelection = !!selectedTerritory && selectedTerritoryStateSet.size > 0;

  return (feature) => {
    if (!hasSelection) return { visible: false };

    const pick = (k) => {
      const v = feature.getProperty(k);
      return v === null || v === undefined ? "" : String(v);
    };

    const rawCandidate =
      pick("STUSPS") ||
      pick("stusps") ||
      pick("postal") ||
      pick("abbr") ||
      pick("STATE") ||
      pick("state") ||
      pick("STATEFP") ||
      pick("statefp") ||
      pick("GEOID") ||
      pick("geoid") ||
      pick("NAME") ||
      pick("name") ||
      "";

    const raw = rawCandidate.trim().toUpperCase();

    const nameToCode = NAME_TO_USPS[raw] || null;

    const digitsOnly = raw.replace(/[^\d]/g, "");
    const fips =
      digitsOnly.length === 1 ? `0${digitsOnly}` : digitsOnly.length === 2 ? digitsOnly : null;
    const fipsToCode = fips ? FIPS_TO_USPS[fips] || null : null;

    const usps = raw.length === 2 ? raw : null;
    const code = usps || fipsToCode || nameToCode || "";

    const on = selectedTerritoryStateSet.has(code);

    return {
      visible: on,
      clickable: false,
      strokeWeight: 2,
      strokeOpacity: 0.9,
      fillOpacity: 0.18,
      strokeColor: "#2563eb",
      fillColor: "#60a5fa",
    };
  };
};

export const buildZipHighlightCircles = (selectedTerritory, enrichedPoints) => {
  if (!selectedTerritory) return [];

  const zips = safeArray(selectedTerritory.zipCodes).map(norm5).filter(Boolean);
  if (!zips.length) return [];

  const zipSet = new Set(zips);

  const groups = new Map();
  (enrichedPoints || []).forEach((p) => {
    const z = norm5(p.zip);
    if (!z || !zipSet.has(z)) return;

    const g = groups.get(z) || { sumLat: 0, sumLng: 0, count: 0 };
    g.sumLat += p.lat;
    g.sumLng += p.lng;
    g.count += 1;
    groups.set(z, g);
  });

  const out = [];
  let i = 0;
  for (const [zip, g] of groups) {
    out.push({
      id: `zip_${zip}`,
      center: { lat: g.sumLat / g.count, lng: g.sumLng / g.count },
      radiusMeters: 12000,
      strokeColor: "#7c3aed",
      fillColor: "#a855f7",
      strokeOpacity: 0.9,
      fillOpacity: 0.18,
      strokeWeight: 2,
    });

    i += 1;
    if (i > 250) break;
  }

  return out;
};

export const buildTerritoryCircles = (territories, enrichedPoints) => {
  if (!territories?.length || !enrichedPoints?.length) return [];

  const palette = ["#60a5fa", "#34d399", "#f97316", "#facc15", "#ec4899", "#a855f7"];

  return territories
    .map((t, index) => {
      const color = palette[index % palette.length];

      const ptsInTerritory = enrichedPoints.filter((p) => accountMatchesTerritory(p, t));
      if (!ptsInTerritory.length) return null;

      const avgLat = ptsInTerritory.reduce((sum, p) => sum + p.lat, 0) / ptsInTerritory.length;
      const avgLng = ptsInTerritory.reduce((sum, p) => sum + p.lng, 0) / ptsInTerritory.length;

      return {
        id: t.id,
        center: { lat: avgLat, lng: avgLng },
        radiusMeters: 32186,
        strokeColor: color,
        fillColor: color,
        strokeOpacity: 0.8,
        fillOpacity: 0.15,
      };
    })
    .filter(Boolean);
};

export const buildStatsByTerritoryId = (territories, enrichedPoints) => {
  const out = {};
  (territories || []).forEach((t) => {
    out[t.id] = {
      accounts: 0,
      samples: 0,
      converted: 0,
      unassigned: 0,
      reps: 0,
      sampleAccounts: 0,
    };
  });

  if (!territories?.length || !enrichedPoints?.length) return out;

  territories.forEach((t) => {
    let accountsCount = 0;
    let samplesCount = 0;
    let convertedCount = 0;
    let unassignedCount = 0;
    let sampleAccountsCount = 0;
    const repIds = new Set();

    enrichedPoints.forEach((p) => {
      if (accountMatchesTerritory(p, t)) {
        accountsCount += 1;
        samplesCount += Number(p.sampleCount || 0);
        convertedCount += Number(p.convertedCount || 0);
        if (Number(p.sampleCount || 0) > 0) sampleAccountsCount += 1;
        if (p.assignedToId) repIds.add(p.assignedToId);
        else unassignedCount += 1;
      }
    });

    out[t.id] = {
      accounts: accountsCount,
      samples: samplesCount,
      converted: convertedCount,
      unassigned: unassignedCount,
      reps: repIds.size,
      sampleAccounts: sampleAccountsCount,
    };
  });

  return out;
};
